import CustomBlock from '@/components/common/custom-block/custom-block';
import DevisActionField from '@/components/common/custom-fields/devis-action-field';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import DevisListActions from '@/components/devis/devis-list-actions';
import DisorderTreatmentCompactList from '@/components/devis/disorder-treatment-compact-list';
import LinkToInterventionButton from '@/components/devis/link-to-intervention-button';
import {
    AVATARS,
    CLIENT_METADATA_AVATAR_TYPE_NAME,
    GROUP_AVATARS,
    INTERVENTION_METADATA_AVATAR_TYPE_NAME,
    PLACES_METADATA_AVATAR_TYPE_NAME,
    ROLE_BIG_DELETE_QUOTE,
    SITES_METADATA_AVATAR_TYPE_NAME,
} from '@/utils/CONST';
import tableRowSx from '@/utils/table-row-sx';
import type { GroupAvatar } from '@/utils/types';
import { Avatar } from '@/utils/types';
import Box from '@mui/material/Box';
import { nth } from 'lodash';
import {
    Datagrid,
    DateField,
    FunctionField,
    Identifier,
    List,
    ReferenceOneField,
    TextField,
    usePermissions,
    useRecordSelection,
    useTranslate,
} from 'react-admin';

export interface DevisListContentProps {
    filter: Record<string, unknown>;
    queryEnabled: boolean;
    loading: boolean;
}

const PLACE_IDENTIFIER_PREFIX = 'P:';

function getPlaceIdentifier(record: GroupAvatar): string | undefined {
    return record?.identifiers?.find((id: string) => id?.startsWith(PLACE_IDENTIFIER_PREFIX));
}

const DevisListContent = ({ filter, queryEnabled, loading }: DevisListContentProps) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const [selectedAvatarsIds, { select }] = useRecordSelection({
        resource: GROUP_AVATARS,
        storeKey: 'contractsTypesList',
    });

    const selectedId = nth(selectedAvatarsIds);
    const hasSelection = !!selectedId;

    const onRowClick = (id: Identifier, _resource: string, _record: any): false => {
        select([id]);
        return false;
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 0.8fr',
                gap: 1,
                gridTemplateRows: '1fr',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <CustomBlock label={translate('routes.devis')} loading={loading}>
                <ListStylesContainer>
                    <List
                        actions={<DevisListActions />}
                        component='div'
                        resource={GROUP_AVATARS}
                        filter={filter}
                        sort={{
                            order: 'DESC',
                            field: 'createdAt',
                        }}
                        empty={false}
                        storeKey='contractsTypesList'
                        queryOptions={{ enabled: queryEnabled }}
                        disableSyncWithLocation
                    >
                        <Datagrid
                            rowClick={onRowClick}
                            rowSx={tableRowSx(selectedAvatarsIds)}
                            bulkActionButtons={false}
                        >
                            <TextField label={'devis.name'} source='name' />

                            <DateField
                                label={'devis.date'}
                                options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                source={'createdAt'}
                                sortable={false}
                            />

                            <FunctionField
                                label={'devis.place'}
                                render={(record: GroupAvatar) => (
                                    <ReferenceOneField
                                        reference={AVATARS}
                                        record={{ id: getPlaceIdentifier(record) ?? '' }}
                                        target='alphaId'
                                        label={false}
                                        filter={{
                                            'metadataAvatarType.name': PLACES_METADATA_AVATAR_TYPE_NAME,
                                            serialization_groups: ['read_avatar_parent'],
                                        }}
                                    >
                                        <TextField source='name' />
                                    </ReferenceOneField>
                                )}
                            />

                            <FunctionField
                                label={'devis.site'}
                                render={(record: GroupAvatar) => (
                                    <ReferenceOneField
                                        reference={AVATARS}
                                        record={{ id: getPlaceIdentifier(record) ?? '' }}
                                        target='alphaId'
                                        label={false}
                                        filter={{
                                            'metadataAvatarType.name': PLACES_METADATA_AVATAR_TYPE_NAME,
                                            serialization_groups: ['read_avatar_parent'],
                                        }}
                                    >
                                        <FunctionField
                                            render={(record: Avatar) => (
                                                <ReferenceOneField
                                                    reference={AVATARS}
                                                    record={{ id: (record.parent as any) ?? '' }}
                                                    target='id'
                                                    label={false}
                                                    filter={{
                                                        'metadataAvatarType.name': SITES_METADATA_AVATAR_TYPE_NAME,
                                                        serialization_groups: ['read_avatar_parent'],
                                                    }}
                                                >
                                                    <TextField source='name' />
                                                </ReferenceOneField>
                                            )}
                                        />
                                    </ReferenceOneField>
                                )}
                            />

                            <FunctionField
                                label={'devis.client'}
                                render={(record: GroupAvatar) => (
                                    <ReferenceOneField
                                        reference={AVATARS}
                                        record={{ id: getPlaceIdentifier(record) ?? '' }}
                                        target='alphaId'
                                        label={false}
                                        filter={{
                                            'metadataAvatarType.name': PLACES_METADATA_AVATAR_TYPE_NAME,
                                            serialization_groups: ['read_avatar_parent'],
                                        }}
                                    >
                                        <FunctionField
                                            render={(record: Avatar) => (
                                                <ReferenceOneField
                                                    reference={AVATARS}
                                                    record={{ id: (record.parent as any) ?? '' }}
                                                    target='id'
                                                    label={false}
                                                    filter={{
                                                        'metadataAvatarType.name': SITES_METADATA_AVATAR_TYPE_NAME,
                                                        serialization_groups: ['read_avatar_parent'],
                                                    }}
                                                >
                                                    <FunctionField
                                                        render={(record: Avatar) => (
                                                            <ReferenceOneField
                                                                reference={AVATARS}
                                                                record={{ id: (record.parent as any) ?? '' }}
                                                                target='id'
                                                                label={false}
                                                                filter={{
                                                                    'metadataAvatarType.name':
                                                                        CLIENT_METADATA_AVATAR_TYPE_NAME,
                                                                }}
                                                            >
                                                                <TextField source='name' />
                                                            </ReferenceOneField>
                                                        )}
                                                    />
                                                </ReferenceOneField>
                                            )}
                                        />
                                    </ReferenceOneField>
                                )}
                            />

                            <ReferenceOneField
                                reference={AVATARS}
                                target={'groupAvatars.id'}
                                label={'devis.intervention'}
                                filter={{
                                    'metadataAvatarType.name': [INTERVENTION_METADATA_AVATAR_TYPE_NAME],
                                    serialization_groups: ['read_avatar_parent'],
                                }}
                            >
                                <FunctionField
                                    render={(record: Avatar) => <LinkToInterventionButton intervention={record} />}
                                />
                            </ReferenceOneField>

                            {permissions?.includes(ROLE_BIG_DELETE_QUOTE) && (
                                <FunctionField render={({ id }: any) => <DevisActionField avatarGroupUriId={id} />} />
                            )}
                        </Datagrid>
                    </List>
                </ListStylesContainer>
            </CustomBlock>

            <CustomBlock label={translate('main.details')} loading={hasSelection && loading}>
                <Box
                    sx={{
                        display: 'grid',
                        height: '100%',
                        overflowY: 'auto',
                        alignContent: 'start',
                    }}
                >
                    {hasSelection && <DisorderTreatmentCompactList selectedId={selectedId} />}
                </Box>
            </CustomBlock>
        </Box>
    );
};

export default DevisListContent;
